'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';

type ClinicDoctifyWidgetProps = {
  scale?: string;
  widgetId?: string;
};

// Stable outside component — never changes, no useMemo needed
const buildDoctifyScriptSrc = (widgetId: string) =>
  `https://www.doctify.com/get-script?widget_container_id=${widgetId}&type=micro-star-widget&tenant=athena-uk&language=en&profileType=practice&layoutType=layoutA&slugs=lips-battersea-power-station&background=transparent`;

export const ClinicDoctifyWidget = ({
  scale = '',
  widgetId = '0ucjlz9v',
}: ClinicDoctifyWidgetProps) => {
  const pathname = usePathname();
  const instanceId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueWidgetId = `${widgetId}${instanceId}`.replace(
    /[^a-zA-Z0-9]/g,
    '',
  );
  const scriptSrc = buildDoctifyScriptSrc(uniqueWidgetId);

  useEffect(() => {
    const container = containerRef.current;
    const target = container?.querySelector(`[id="${uniqueWidgetId}"]`);
    if (!container || !target) return;

    target.innerHTML = '';
    let script: HTMLScriptElement | undefined;

    const loadWidget = () => {
      if (script) return;

      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.dataset.doctifyWidgetId = uniqueWidgetId;
      document.body.appendChild(script);
    };

    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver === 'undefined') {
      loadWidget();
    } else {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            loadWidget();
            observer?.disconnect();
          }
        },
        { rootMargin: '200px' },
      );
      observer.observe(container);
    }

    return () => {
      observer?.disconnect();
      script?.remove();
      target.innerHTML = '';
    };
  }, [pathname, scriptSrc, uniqueWidgetId]);

  return (
    <div
      className={`flex h-[110px] w-[220px] items-center justify-center ${scale}`}
    >
      <div
        ref={containerRef}
        className="flex w-full origin-center justify-center"
      >
        <div id={uniqueWidgetId} className="h-[110px] w-[220px]" />
      </div>
    </div>
  );
};

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: Element, reload?: boolean) => void;
    };
  }
}

export const ClinicTrustPilotWidget = ({ scale = '' }: { scale?: string }) => {
  const pathname = usePathname();
  const containerRef = useRef<HTMLDivElement>(null);
  // Gates script + widget activation on near-viewport rather than mount, so
  // below-the-fold instances (WhyChooseSection, RateYourExperienceSection)
  // don't compete for main-thread time during initial load. Instances
  // already in the initial viewport (the hero trust-logo row, via
  // BaseHeroSection/ServiceHeroSection) intersect immediately on observe,
  // so this doesn't delay them — same load timing as before for those.
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    setShouldLoad(false);
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [isMounted, pathname]);

  useEffect(() => {
    if (!shouldLoad) return;
    const el = containerRef.current;
    if (!el) return;

    // Retry until window.Trustpilot is available (script may still be loading)
    let attempts = 0;
    const MAX_ATTEMPTS = 20;
    const INTERVAL_MS = 150;
    let timeoutId: number | undefined;

    const tryLoad = () => {
      if (window.Trustpilot) {
        window.Trustpilot.loadFromElement(el, true);
        return;
      }
      if (attempts < MAX_ATTEMPTS) {
        attempts++;
        timeoutId = window.setTimeout(tryLoad, INTERVAL_MS);
      }
      // Silently give up after ~3s — widget just won't show, no crash
    };

    tryLoad();

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [shouldLoad, pathname]);

  return (
    <div className={`flex h-[115px] w-[220px] justify-center ${scale}`}>
      {isMounted && (
        <div
          ref={containerRef}
          className="trustpilot-widget h-[115px] w-[220px] max-w-full"
          data-locale="en-GB"
          data-template-id="53aa8807dec7e10d38f59f32"
          data-businessunit-id="5a53943eb894c90a88f68556"
          data-style-height="115px"
          data-style-width="100%"
        >
          <a
            href="https://uk.trustpilot.com/review/lips.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Trustpilot reviews for LIPS Healthcare"
          >
            <span className="sr-only">Trustpilot reviews</span>
          </a>
        </div>
      )}
      {shouldLoad && (
        <Script
          key="trustpilot-script"
          src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          strategy="lazyOnload"
          onLoad={() => {
            const el = containerRef.current;
            if (el && window.Trustpilot)
              window.Trustpilot.loadFromElement(el, true);
          }}
        />
      )}
    </div>
  );
};
